<article class="rules-document">
  <header>
    <h1>Terms and Conditions for Participation in Auctions</h1>
    <p class="subtitle">(Public Offer)</p>
    <p class="last-updated">Last updated: <time datetime="2025-12-12">December 12, 2025</time></p>
  </header>

  <nav class="table-of-contents">
    <h2 class="toc-title">Table of Contents</h2>
    <ul class="toc-list">
      <li>
        <a role="button" (click)="scrollToSection($event, 'general-provisions')">General Provisions</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'platform')">Platform</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'platform-usage')"
          >Use of the Platform Through Employees and Third Parties</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'platform-access')">Access to the Platform</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'deposit-refund')">Deposit Refund</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'auction-types')">Types of Auctions</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'auction-sheet')">Auction Sheet</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'translation')"
          >Auction Sheet Translation</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'corrections')"
          >Auction Sheet Corrections</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'preliminary-inspections')"
          >Pre-Auction Inspections</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'compliance-check')"
          >Compliance Check</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'bidding-rules')">Bidding Rules</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'lot-withdrawal')"
          >Lot Withdrawal</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'purchase-cancellation')"
          >Cancellation of an Erroneous Purchase</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'seller-refusal')"
          >Seller Refusal to Sell</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'relisting')"
          >Re-Listing the Vehicle for Auction</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'additional-transport')"
          >Additional Transportation Costs in Japan</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'island-vehicles')"
          >Vehicles from Island Territories</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'claims')">Claims to the Auction House</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'content')">Content</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'terms-update')">Updates to the Terms</a>
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'communications')"
          >Communications and Disputes</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'applicable-law')"
          >Governing Law and Jurisdiction</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'language')"
          >Language and Priority of Versions</a
        >
      </li>
      <li>
        <a role="button" (click)="scrollToSection($event, 'final-provisions')"
          >Final Provisions</a
        >
      </li>
    </ul>
  </nav>

  <section id="general-provisions">
    <h2>General Provisions</h2>

    <p>
      Welcome to the auction platform www.jpauc.online (the "Platform"). These Terms and Conditions for Participation in Auctions (the "Terms") govern your use of the Platform, as well as all transactions related to the purchase of vehicles through the Platform, which is operated by the Platform operator (the "Operator").
    </p>

    <p>
      These Terms constitute a public offer. Before you begin using the Platform, you must review these Terms and confirm your acceptance by ticking the "I agree" checkbox. By continuing to use the Platform, you confirm that you have read, understood, and unconditionally agree to comply with these Terms.
    </p>
  </section>

  <section id="platform">
    <h2>Platform</h2>

    <p>
      The Platform provides users with access to auctions held at actual auction venues in Japan through an authorized Operator. The Operator is an official member of the relevant auction houses and participates in auctions on your behalf.
    </p>

    <p>
      The Platform receives information about vehicles offered at actual Japanese auctions. The data is transmitted to the Platform via integrated services.
    </p>

    <p>
      Lots are published in the form in which they are provided by the auction houses. The Platform does not modify the description, condition, or parameters of any lot. Accordingly, the Platform and the Operator do not provide any warranties as to the accuracy, completeness, or reliability of lot information and are not liable for any discrepancies, errors, or omissions arising from the use of such information.
    </p>

    <p>
      You select a lot and indicate the maximum amount you are willing to bid. Your bid is recorded in the Platform system.
    </p>

    <p>
      The Operator bids at the auction on your behalf in accordance with the rules of the relevant auction venue. If your bid wins, you receive confirmation via the Platform. If your bid does not win, your participation is deemed completed with no payment obligation.
    </p>
  </section>

  <section id="platform-usage">
    <h2>Use of the Platform Through Employees and Third Parties</h2>

    <p>
      Registration and use of the Platform is carried out by you as the registered counterparty (a legal entity or an individual entrepreneur), and all transactions, purchases, and obligations arising from participation in auctions are registered in your name.
    </p>
    <p>
      You may, through your personal account, create, manage, and delete additional user accounts (logins) for your employees, representatives and/or clients for the purpose of placing bids and performing other actions within the Platform.
    </p>
    <p>
      All actions performed using the accounts created by you, including but not limited to: placing, changing and confirming bids; participation in auctions, negotiations and live auctions; confirmation of translations, pre-auction inspections, and auction sheet corrections; expressing consent to purchase, cancellation, or re-auction terms—are deemed to be performed by you, regardless of who actually carried them out (an employee, representative, client, or any other third party).
    </p>
    <p>
      You bear full and unconditional responsibility for all obligations, financial consequences, penalties, costs, and losses arising from the actions of any persons who obtained access to the Platform through accounts created by you.
    </p>
    <p>
      Any internal disputes between you and your employees, representatives, or clients must be resolved by you independently and do not affect your obligations to the Platform, the Operator, and the auction houses.
    </p>
  </section>

  <section id="platform-access">
    <h2>Access to the Platform</h2>

    <p>
      Access to the Platform is provided subject to you having either an active deposit or an approved credit line.
    </p>
    <p>
      To maintain access to the Platform functionality, you must keep a sufficient deposit balance in your account. If the deposit is fully used or the remaining balance becomes insufficient for further participation in auctions, access to the Platform is automatically restricted until the deposit is replenished.
    </p>
    <p>
      If you use an approved credit line, access to the Platform is automatically suspended once you reach or exceed the established limit. Access is restored after the outstanding amount is repaid or the available credit limit is reinstated.
    </p>
    <p>
      The Platform reserves the right to automatically restrict or suspend access without prior notice in the event of insufficient funds, exceeding the credit limit, or other violations of financial requirements.
    </p>
  </section>

  <section id="deposit-refund">
    <h2>Deposit Refund</h2>

    <p>
      A deposit refund is possible only if there is no exposure on the account. Refunds are made upon your written request exclusively to the original bank account from which the transfer was made and in your name. All bank and currency conversion fees are borne by you. A refund request is processed within 10 business days from the date of the written request.
    </p>

    <p>
      The Operator may offset the deposit against auction fees, penalties, re-auction costs, shortages, logistics, inspections, or bank fees. The deposit may also be withheld in the event of non-payment, refusal to complete a purchase, fraud, fictitious bids, collusion, or other violations of these Terms.
    </p>
  </section>

  <section id="auction-types">
    <h2>Types of Auctions</h2>

    <p>
      The Platform provides access to various auction formats used by Japanese auction houses. Each format has its own rules, bidding procedure, and winner determination conditions.
    </p>

    <section class="subsection">
      <h3>Standard Auctions</h3>

      <p>
        Standard auctions are the primary format at Japanese auto auctions. Bidding takes place in real time and bids are raised step-by-step. The Operator increases bids on your behalf within the limit set by you. The winner is the participant who offers the highest bid at the moment the auction ends.
        </p>
        <p>
          Most vehicles have a hidden reserve price set by the seller. Until the reserve price is reached, the auction does not guarantee that the vehicle will be sold. The final price may be lower than your maximum bid, but the vehicle may remain unsold if the seller's reserve price is higher than the offered amount and there is only one bidder remaining.
        </p>
    </section>

    <section class="subsection">
      <h3>Tender Auctions</h3>

      <p>
        In tender auctions, all bids are sealed and not visible to other participants. You set your maximum bid, and the Operator submits it in tender format without incremental increases. The auction house selects the winner based on all submitted offers. Since the tender bid is submitted as a final price offer, purchasing below your submitted limit is not possible. Once submitted, a tender bid cannot be changed or canceled.
      </p>
    </section>

    <section class="subsection">
      <h3>Satellite Auctions</h3>

      <p>
        Satellite auctions are remote auction venues operated under the main auction house. The auction format is similar to standard auctions, and the terms of sale and confirmation rules fully follow the main venue requirements. Due to the location of satellite venues, the cost of transporting vehicles may differ from the main venue. All logistics costs are payable by you.
      </p>
    </section>

    <section class="subsection">
      <h3>AUCNET and MOTA Auctions</h3>

      <p>
        AUCNET and MOTA are virtual auction systems that do not have their own yards or physical venues. Lots are listed by third-party sellers and dealers located across Japan. These systems may have separate sections and categories with higher-than-standard auction fees. The terms of such categories, including commission amounts and timelines for vehicle and document transfer, are determined by the third-party supplier. All additional costs and risks associated with participating in such categories—including higher auction fees, higher transportation costs, and other required payments—are borne by you.
      </p>
    </section>

    <section class="subsection">
      <h3>One-Price (Fixed-Price) Purchases</h3>

      <p>
        Some auction venues offer a One-Price format where a vehicle is listed at a fixed price without bidding. In this format, the first participant to accept the stated price obtains the right to purchase the lot. Auction houses apply a higher auction fee for One-Price lots, as determined by the relevant venue. All additional expenses, including the increased auction fee, are borne by you.
      </p>
    </section>

    <section class="subsection">
      <h3>Purchase via Negotiation</h3>

      <p>
        If the seller's reserve price is not reached during a standard auction, the auction house may offer a negotiation purchase format. Negotiations are available only after the auction ends, within the time period set by the auction house, and only if the reserve price was not reached.
        </p>
        <p>
          The Platform and the Operator enter into negotiations only upon your request submitted through the Platform. The Operator is entitled to purchase the lot at a price not exceeding your original maximum bid used during the auction, unless you specify otherwise. If the negotiation purchase is successful, auction houses apply an increased auction fee. All additional expenses, including the increased auction fee, are borne by you.
        </p>
        <p>
          The Platform and the Operator do not guarantee a successful negotiation outcome, do not influence the seller's decision, do not control review timelines, and are not liable for the seller's refusal.
        </p>
    </section>

    <section class="subsection">
      <h3>Reverse Negotiation Purchase</h3>

      <p>
        Reverse negotiation purchase is a format where, after an auction ends without reaching the reserve price, the seller makes a counteroffer. In reverse negotiations: the seller specifies the price at which they are willing to sell the vehicle; the offer may be sent to one or several participants who previously took part in the auction; and the offer is valid for a limited time set by the auction house.
      </p>

      <p>
        The Operator may independently decide to purchase the vehicle via reverse negotiations without additional approval from you if the seller's offered price is equal to or lower than your previously recorded maximum bid.
      </p>

      <p>
        Reverse negotiation purchase is not a continuation of the standard auction and does not grant you any preferential rights other than those provided by the relevant auction house rules.
      </p>
    </section>

    <section class="subsection">
      <h3>Live Auctions with Video Streaming</h3>

      <p>
        Live auctions with video streaming are a format in which the Operator participates at the auction venue in real time and provides you with a video broadcast with the ability to increase a previously submitted bid.
      </p>

      <p>
        This format is a paid additional service and is provided only by prior agreement. Live video streaming is not a standard method of participating in auctions. The video broadcast is for informational support only and does not replace the Platform functionality.
      </p>
      <p>
        You acknowledge and agree that the video stream may be delayed; technical failures, reduced image quality, interruptions, or desynchronization with the actual auction may occur; and the Platform and Operator do not guarantee complete alignment between the broadcast and the real auction stage.
      </p>
      <p>
        If the video stream is interrupted, bid increase confirmation is not received in time, or the opportunity to raise a bid is missed, the Operator may continue bidding based on the last confirmed bid or stop raising bids to minimize risks.
      </p>
      <p>
        All bids and instructions confirmed by you during a live video auction are legally binding. The use of video streaming does not release you from payment obligations if you win the lot.
      </p>
      <p>
        The Platform and the Operator are not liable for losing a lot due to delays, technical failures, or streaming limitations and do not compensate for any losses caused by inability to raise a bid in time.
      </p>
    </section>
  </section>

  <section id="auction-sheet">
    <h2>Auction Sheet</h2>

    <p>
      An auction sheet is an auction house document describing the condition of a vehicle at the time of inspection. Information in the auction sheet is provided by the seller and the auction house inspector and is the primary source for purchase decisions. The Platform publishes auction sheets exactly as received from the relevant auction systems without changes. The Platform and the Operator do not confirm the accuracy or completeness of auction house information and are not liable for hidden defects not indicated in the auction sheet.
    </p>

    <p>An auction sheet typically includes (but does not guarantee the presence of) the following:</p>
    <dl>
      <dt>Identification information:</dt>
      <dd>make, model, year, chassis number / VIN.</dd>

      <dt>Technical specifications:</dt>
      <dd>transmission type, drivetrain, fuel type, trim/options.</dd>

      <dt>Overall condition:</dt>
      <dd>exterior and interior condition grades.</dd>

      <dt>Structural damage:</dt>
      <dd>
        information about structural repairs, restored elements, or accident traces.
      </dd>

      <dt>Damage map:</dt>
      <dd>dents, scratches, rust, deformation, and other body defects.</dd>

      <dt>Inspector comments:</dt>
      <dd>
        noises, odors, technical notes, modifications, tuning, and other observations.
      </dd>
    </dl>

    <p>
      Vehicle grades are assigned by the auction house and reflect overall technical and visual condition. The Platform and Operator provide no warranties regarding subjective auction inspector evaluations.
    </p>
  </section>

  <section id="translation">
    <h2>Auction Sheet Translation</h2>

    <p>The Platform provides two types of auction sheet translations:</p>
    <ul>
      <li>Manual translation — performed by Platform staff upon request.</li>
      <li>
        Automatic AI translation — provided for all lots available through integrated auction systems.
      </li>
    </ul>

    <section class="subsection">
      <h3>Manual Translation</h3>

      <p>
        Manual translations are provided upon request and require additional processing time. Your request must be submitted no later than 3 hours before the start time of the lot's auction. Requests submitted after this deadline may be rejected or completed without guaranteeing availability before bidding. The translation is published under your lot in the Platform.
      </p>

      <p>
        If it is not possible to contact you through all available methods, the bid will not be placed in order to avoid purchase risks without a confirmed translation.
      </p>

      <p>
        The Platform is responsible for the correctness of manual translations within the limits of linguistic interpretation of the text contained in the auction sheet.
      </p>

      <p>
        Manual translations are performed by translators, not technical experts. Therefore, a manual translation is not a technical diagnosis, does not confirm the mechanical condition of the vehicle, does not interpret the severity of damage, does not clarify causes of malfunctions, and does not determine wear level or repair needs. The translator conveys only the meaning of the inspector's text without technical assessment or interpretation. The Platform is not liable for any decisions based on technical assumptions made from the translation.
      </p>

      <p>
        You are responsible for reviewing the original auction sheet, checking damage diagrams and symbols, interpreting technical codes and marks, and making purchase decisions based on the auction sheet rather than the translation. The Platform is not obligated to explain auction symbols and is not liable for your interpretation.
      </p>
    </section>

    <section class="subsection">
      <h3>Automatic AI Translation</h3>

      <p>
        AI translation is provided automatically for informational purposes only and may contain errors in text recognition, wording, and symbols. Lots without a manual translation request do not require your confirmation and are bid automatically. If you purchase a vehicle without requesting manual translation, all responsibility for understanding the AI translation lies with you.
      </p>

      <p>
        The Platform does not guarantee and is not liable for the accuracy or completeness of AI translations, differences between translation and the original sheet, or incorrect interpretation of the AI translation.
      </p>
    </section>
  </section>

  <section id="corrections">
    <h2>Auction Sheet Corrections</h2>

    <p>
      Corrections are official updates published by the auction house after the initial listing and before or during the auction. They may relate to any auction sheet information, including but not limited to mileage, grade, accident history, and inspector comments.
    </p>

    <p>
      Corrections are published only by the auction house. The Platform and Operator transmit them as received from the auction system. You will receive a notification. Notifications are an auxiliary feature; the Platform is not liable if you do not receive or see a notification in time. You must independently monitor corrections and their content before the auction via the Platform.
    </p>

    <p>
      If a correction affects material vehicle characteristics (mileage, accidents, structural elements, grade), the Platform or the Operator may request additional confirmation of your intent to bid. If it is not possible to contact you through all available methods, the bid may be rejected to avoid disputed purchase risks.
    </p>

    <p>
      The Platform and Operator are not liable for the content, timing, or completeness of corrections published by the auction house, do not guarantee that corrections will be published before bidding, and are not liable for losses arising from late publication by the auction house.
    </p>

    <p>
      Corrections are part of official auction data. The presence or absence of corrections does not create additional cancellation rights unless explicitly provided by the relevant auction house rules.
    </p>
  </section>

  <section id="preliminary-inspections">
    <h2>Pre-Auction Inspections</h2>

    <p>
      Auction venues (with exceptions) provide a paid pre-auction inspection service on the auction day. You may request such an inspection through the Platform no later than 3 hours before the lot's auction time. All costs related to the inspection are borne by you. The price, scope, and content of the service are determined by the relevant auction house or its contractor.
    </p>

    <p>
      If, after the Operator receives the inspection results, it is not possible to contact you through all available methods, the bid will not be approved to avoid purchase risks without confirmed inspection results.
    </p>

    <p>
      By confirming a bid, you agree to accept the vehicle in its condition at the time of the auction, confirm that you have reviewed all available information, assume the risks of hidden defects, incomplete data, possible contractor/auction errors, and waive claims against the Platform and the Operator based on differences between expectations and the vehicle's actual condition.
    </p>
  </section>

  <section id="compliance-check">
    <h2>Compliance Check</h2>

    <p>
      You bear full responsibility for confirming that the vehicle meets the import requirements of your country, including but not limited to:
    </p>
    <ul>
      <li>permissible model year;</li>
      <li>
        environmental and technical standards, including emissions, design requirements, and certification;
      </li>
      <li>
        regulatory and legal restrictions applicable to certain vehicle types;
      </li>
      <li>customs rules, restrictions, and procedures;</li>
      <li>sanctions and permit regimes in your country.</li>
    </ul>

    <p>
      You must independently confirm import eligibility before placing a bid. All risks related to inability to import, register, or operate the vehicle in your country are fully borne by you. This includes cases where the vehicle does not pass required inspections or certifications in Japan under your country's rules.
    </p>
  </section>

  <section id="bidding-rules">
    <h2>Bidding Rules</h2>

    <section class="subsection">
      <h3>Bid Submission Deadlines</h3>

      <p>
        For standard auctions, bids are recommended to be submitted no later than 1 hour before the lot's auction starts. Bids submitted later may be accepted by the Platform, but the Platform does not guarantee processing or successful transmission to the auction.
      </p>

      <p>
        Tender bids are accepted strictly until 13:00 (Japan time) on the auction day. Requests submitted after 13:00 are not accepted or processed, regardless of circumstances. The Platform is not liable for inability to submit a tender bid after the deadline.
      </p>

      <p>
        Auction houses may change auction start times without notice. Such changes may affect the ability to submit, modify, or cancel a bid. The Platform is not liable for consequences caused by rescheduling, acceleration of auction start, technical/organizational changes by the auction house, or inability to submit/modify/cancel bids due to such changes.
      </p>
    </section>

    <section class="subsection">
      <h3>Bid Placement</h3>

      <p>
        All operations related to bids—such as placing bids, requests to modify or cancel bids, confirmation to participate after translation, confirmation after pre-auction inspection, and requests for negotiation purchase—must be performed only through the Platform interface.
      </p>

      <p>
        The Platform does not accept or process bids or confirmations sent by phone, WhatsApp, Viber, Telegram, email, or any other method outside the Platform. Any bids or instructions delivered via such channels are not considered valid, are not processed, and create no obligations for the Platform.
      </p>
    </section>

    <section class="subsection">
      <h3>Protective Overbid Step</h3>

      <p>
        To reduce the risk of losing by a minimal bid difference, the Platform may apply a protective overbid step:
      </p>
      <ul>
        <li>Up to ¥300,000 — automatic overbid up to ¥25,000;</li>
        <li>Above ¥300,000 — automatic overbid up to ¥50,000.</li>
      </ul>

      <p>
        This protective overbid is used solely to increase the probability of winning and is not a purchase guarantee. Due to the speed and volatility of auctions, minor deviations between your requested maximum bid and the actual purchase amount may occur. The Platform is not liable for minimal limit exceedances caused by auction dynamics.
      </p>
    </section>

    <section class="subsection">
      <h3>Group Bids</h3>

      <p>
        A group bid is a mechanism that allows you to place bids on multiple lots where purchasing any one of them is acceptable. The Platform does not limit the number of lots in a group, the number of groups, or the number of target purchases within one or more groups.
      </p>

      <p>
        To reduce the risk of purchasing multiple vehicles at once, the Platform recommends that the time difference between the auction start times of lots in the same group be at least 30 minutes. The Platform may accept groups with a smaller interval; however, you bear all risks, including simultaneous purchases.
      </p>

      <p>
        The Platform does not guarantee auction start times match the published schedule because auction houses may change timing and auction speed. Tender auctions cannot be included in group bids.
      </p>
    </section>

    <section class="subsection">
      <h3>Internal Competing Bids</h3>

      <p>
        If multiple clients place bids on the same vehicle, the Platform applies a maximum-bid prioritization mechanism. This reflects the principle of Japanese auction houses where the highest offer wins.
      </p>

      <p>
        When multiple internal bids exist for the same lot, priority is given to the client who set the higher maximum limit. The lower maximum bid is automatically considered secondary and is not used unless it exceeds another client's bid.
      </p>

      <p>
        If there are competing bids within the Platform: you do not see the other client's bid amount; you receive a notification that a higher bid exists for this vehicle.
      </p>

      <p>
        The minimum increment to outbid is ¥30,000. The number of outbids is not limited.
      </p>

      <p>
        It is recommended to increase your bid no later than 1 hour before the auction starts. Requests to increase submitted later may be accepted or rejected. If accepted, the increased bid is considered active and will be bid.
      </p>

      <p>
        You receive a Telegram notification each time your bid is outbid by an opponent. Upon receiving such a notification, you may increase your bid or take no action, in which case your bid remains unchanged and, if purchased, the lot will be assigned to the client with the highest bid even if your bid is within the winning price range.
      </p>

      <p>
        You are responsible for timely bid increases, monitoring notifications, making decisions to raise or not raise bids, and understanding that no response to a notification may result in losing the auction. The Platform is not liable if you fail to respond in time or if you outbid less than 1 hour before the auction and the Platform did not have time to process it. Notifications are an auxiliary feature and the Platform is not liable if you did not receive or see a notification in time.
      </p>

      <p>
        If internal competing bids exist for the same lot, participation via live video auction format is not provided. This restriction is due to the need to ensure equal conditions for all participants and to comply with maximum-bid prioritization.
      </p>
    </section>

    <section class="subsection">
      <h3>Bid Cancellation or Modification</h3>

      <p>
        Cancellation or modification of an approved bid in the Platform is possible only if the auction house allows cancellation and if time permits before the auction. The Platform does not guarantee the possibility of cancellation or modification even if you submit the request in time.
      </p>
    </section>

    <section class="subsection">
      <h3>Liability</h3>

      <p>
        Bids may be rejected, not processed, or missed due to data transmission delays, auction schedule changes, technical work by the auction house, network/system failures, and other factors. The Platform and Operator are not liable if a lot is missed due to factors beyond their control.
      </p>
    </section>

    <section class="subsection">
      <h3>Binding Nature of a Bid</h3>

      <p>
        Any bid entered by you through the Platform is considered valid regardless of errors, typos, or incorrect amount entry. Each bid submitted through the Platform is a legally binding offer to purchase a vehicle. If your bid wins, you automatically assume the obligation to pay for and purchase the vehicle under the auction house terms.
      </p>
    </section>
  </section>

  <section id="lot-withdrawal">
    <h2>Lot Withdrawal</h2>

    <p>
      Lot withdrawal means removal of a vehicle from an auction at the initiative of the auction house or the seller before the end of bidding for that lot. A lot may be withdrawn for various reasons, including but not limited to: seller withdrawal; errors/inconsistencies in the auction sheet; technical/organizational reasons by the auction house; a change in the seller's decision; or other circumstances under auction rules.
    </p>

    <p>
      The decision to withdraw a lot is made solely by the auction house. The Platform and Operator do not influence this decision and do not guarantee that the lot will remain listed until the auction ends.
    </p>
    <p>
      If a lot is withdrawn: all previously submitted bids are canceled; the purchase is deemed not completed; you have no payment obligation for the lot; and any account exposure related to the lot is released.
    </p>
    <p>
      The Platform and Operator are not liable for withdrawal reasons, do not compensate lost profits or other indirect losses related to withdrawal, and do not guarantee re-listing of the vehicle at future auctions. If the lot was withdrawn after you submitted a bid but before the auction ended, this does not give you the right to file claims against the Platform or Operator.
    </p>
  </section>

  <section id="purchase-cancellation">
    <h2>Cancellation of an Erroneous Purchase</h2>

    <p>
      Cancellation after winning an auction is not available at all auction houses and is governed exclusively by the rules of the relevant auction house. The Platform does not decide on cancellation and does not influence penalty amounts or application.
    </p>

    <section class="subsection">
      <h3>General Cancellation Terms</h3>

      <p>
        Most Japanese auction houses allow cancellation only within a limited time period of 30 to 120 minutes after the lot's auction ends. Time limits and cancellation availability are determined by the auction house (not the Platform) and may vary by venue, lot category, and auction format. After the allowed time expires, cancellation is not possible under any circumstances.
      </p>

      <p>
        The Platform does not guarantee that a cancellation request can be submitted within the allowed time because processing depends on the auction house response time and administrative procedures.
      </p>

      <p>
        Some lot categories may not be eligible for cancellation, including but not limited to: lots marked by the auction house as having serious damage or major repairs; special categories where cancellation is explicitly prohibited; and purchases made via negotiation format where cancellation is typically not allowed.
      </p>

      <p>
        Cancellation rules are determined solely by the auction house and may vary by venue, lot category, and auction type.
      </p>
    </section>

    <section class="subsection">
      <h3>Cancellation Penalties</h3>

      <p>
        Upon cancellation, the auction house charges a combined penalty, typically ¥100,000 for standard lots and ¥150,000 for expensive vehicles. The actual amount may vary depending on the auction house rules, auction format, and request timing.
      </p>

      <p>
        The penalty is paid entirely by the buyer and must be paid within the specified deadline. The Platform does not compensate for penalties and does not participate in their adjustment.
      </p>
    </section>
  </section>

  <section id="seller-refusal">
    <h2>Seller Refusal to Sell</h2>

    <p>
      Under Japanese auction rules, the seller retains the right to refuse the sale after the auction ends in cases allowed by the auction house. The Platform does not control seller decisions and is not liable for cancellation initiated by the seller. The Platform transmits auction house decisions as-is and does not participate in negotiations.
    </p>

    <p>
      If the seller cancels the sale, your deposit remains in your account and may be used for future auctions. Deposit refunds are handled according to the Platform's general terms. The Platform does not compensate losses related to seller cancellation.
    </p>
  </section>

  <section id="relisting">
    <h2>Re-Listing the Vehicle for Auction</h2>

    <p>
      Re-listing the vehicle for auction may be used when, after you have won the bidding, you request to cancel the purchase, but a standard cancellation is not available or its application results in disproportionate financial costs.
    </p>

    <p>
      Re-listing is not a cancellation and does not guarantee release from financial obligations. This mechanism is used only as an alternative way to minimize potential losses.
    </p>
    <p>
      In the event of re-listing: the initial purchase is considered completed and cannot be canceled; all costs related to re-listing, including auction fees, penalties, administrative costs and other payments, are borne by you.
    </p>
    <p>
      If the resale price after re-listing is lower than the original purchase price, you must pay the difference between the original purchase price and the resale price, as well as all related costs established by the auction house.
    </p>
    <p>
      The Platform and Operator do not guarantee successful resale, do not guarantee achieving any particular price, are not liable for the financial outcome of re-listing, and do not compensate for losses resulting from the repeated auction.
    </p>
  </section>

  <section id="additional-transport">
    <h2>Additional Transportation Costs in Japan</h2>

    <p>
      Vehicles purchased through the Platform may incur additional transportation and related costs within Japan. All such costs are paid by you.
    </p>
    <p>
      Additional costs may arise, including but not limited to:
    </p>
    <ul>
      <li>
        vehicles located in yards far from the main auction yard or logistics hubs;
      </li>
      <li>vehicles with lowered or lifted suspension;</li>
      <li>vehicles not in drivable condition;</li>
      <li>vehicles over 2 meters in height;</li>
      <li>vehicles with roof racks or other protruding elements;</li>
      <li>
        vehicles with serious oil/fuel/coolant/other fluid leaks;
      </li>
      <li>
        vehicles purchased via negotiations, One-Price sales, or AUCNET/MOTA-type systems;
      </li>
      <li>trucks, buses, special and commercial vehicles;</li>
      <li>
        cases requiring handling equipment, tow trucks, winches, or special moving tools.
      </li>
    </ul>
    <p>
      All such and other additional costs related to movement, storage, handling, and preparation for shipment are fully borne by you. The Platform and Operator are not obligated to estimate or fix such costs in advance.
    </p>
  </section>

  <section id="island-vehicles">
    <h2>Vehicles from Island Territories</h2>

    <p>
      Vehicles purchased in island territories of Japan must be transported to the mainland, typically by ferry, before further logistics processing.
    </p>

    <p>
      If the vehicle does not meet ferry requirements, alternative, more expensive transportation methods may be applied at your expense. The Platform and Operator are not liable for inability to use ferry transport and do not compensate expenses caused by the vehicle not meeting requirements.
    </p>

    <section class="subsection">
      <h3>Minimum Requirements for Ferry Transport</h3>

      <p>
        For ferry transport, the vehicle must meet the following minimum requirements: ground clearance of at least 15 cm; the hood opens normally; the vehicle is drivable (engine starts, transmission functions, vehicle can move on its own); steering and tires are functional; braking system (service brake and parking brake) is functional; the vehicle can drive on/off by itself without towing/winch/crane/other assistance; windshield is intact; side and rear windows are installed and close; interior space (cabin, trunk, roof boxes) is completely empty and contains no personal items, parts, or other cargo.
      </p>
    </section>

    <section class="subsection">
      <h3>Ferry Transport Restrictions</h3>

      <p>
        Ferry transport is not permitted if any of the following apply: engine does not start or the vehicle cannot move on its own; presence of white or black smoke from the engine, exhaust system, or other units; oil/fuel/coolant/other fluid leaks; damaged/defective/unstable hybrid or EV batteries; missing windshield or missing/damaged/poorly fixed side or rear windows; presence of any foreign objects or additional cargo in the cabin, trunk, or body.
      </p>
    </section>
  </section>

  <section id="claims">
    <h2>Claims to the Auction House</h2>

    <p>
      Claims regarding purchased vehicles may be filed only under the rules of the relevant auction house. The Platform and Operator do not independently decide on admissibility, content, or satisfaction of claims and act only as an intermediary for transmitting them.
    </p>

    <p>
      Claims are allowed only in cases and within time limits explicitly provided by the rules of the specific auction venue. Failure to comply with deadlines or procedures automatically deprives you of the right to claim review.
    </p>

    <p>
      A claim is submitted only at your initiative through your personal account after you submit the relevant request. The Platform and Operator do not initiate claims without your direct request. Claims may be submitted only if all requirements and procedures set by the auction house are met.
    </p>

    <section class="subsection">
      <h3>Claim Submission Time Limits</h3>

      <p>Typical claim deadlines (may vary by auction house):</p>
      <ul>
        <li>exterior/interior discrepancies — on the auction day;</li>
        <li>registration/specification errors — up to 7 calendar days;</li>
        <li>accident/structural damage — up to 10 calendar days;</li>
        <li>engine/transmission defects — up to 15 calendar days.</li>
      </ul>

      <p>
        Final deadlines and review procedures are determined solely by auction house rules.
      </p>
    </section>

    <section class="subsection">
      <h3>Claim Limitations and Exclusions</h3>

      <p>
        Claims are considered only within the limits and under the conditions set by the relevant auction house. In the cases below, claims are not accepted, are limited, or may be rejected without substantive review regardless of defect nature.
      </p>

      <section class="subsubsection">
        <h4>Lots with no claim rights</h4>

        <p>
          Special categories sold without claims (e.g., Accident, Low Price, White Corner, and similar), and categories explicitly sold "as-is." The classification is determined solely by the auction house and cannot be revised.
        </p>
      </section>

      <section class="subsubsection">
        <h4>Vehicle age/mileage thresholds</h4>

        <p>
          Where auction rules limit claims (often 10 years and/or 150,000 km, or other thresholds), claims on major units and/or cancellation may be limited or subject to special compensation/cost-sharing terms.
        </p>
      </section>

      <section class="subsubsection">
        <h4>Defects disclosed in auction documentation</h4>

        <p>
          No claims for any defects/features/discrepancies indicated in the auction sheet, auction sheet corrections, inspector comments, or obvious from visual inspection or logically following from the description.
        </p>
      </section>

      <section class="subsubsection">
        <h4>Engine/transmission/hybrid/EV systems</h4>

        <p>
          If symptoms or malfunctions are already noted in the auction sheet, claims on these units are often excluded or only considered in exceptional cases at the auction's discretion.
        </p>
      </section>

      <section class="subsubsection">
        <h4>Modifications/repairs/interference</h4>

        <p>
          No claims if the vehicle was modified, tuned, retrofitted with non-standard parts, shows signs of non-standard repair/restoration/structural changes, or was sold under special formats (negotiation, special sales, specific auction categories).
        </p>
      </section>

      <section class="subsubsection">
        <h4>Export and status changes</h4>

        <p>
          No claims if the vehicle has been exported from Japan, customs-cleared or otherwise processed making inspection impossible, transferred to third parties, or altered in ways preventing objective verification.
        </p>
      </section>

      <section class="subsubsection">
        <h4>Repeat claim on the same basis</h4>

        <p>
          Repeated claims (second and subsequent) may be limited or rejected unless permitted by auction rules (except certain critical cases).
        </p>
      </section>

      <section class="subsubsection">
        <h4>Compensation form and limits</h4>

        <p>
          Even when a claim is allowed, auction rules may cap compensation/price reduction (especially for inexpensive lots) and set special cost allocation rules (e.g., transport and auction fees) in case of cancellation/return.
        </p>
      </section>

      <section class="subsubsection">
        <h4>Final note</h4>

        <p>
          All vehicles are purchased "as-is" unless auction house rules explicitly provide otherwise. The Platform and Operator do not expand claim eligibility, do not change claim review rules, and are not liable for the auction house refusal to accept or satisfy a claim.
        </p>

        <p>
          The auction house decision on a claim is final and binding.
        </p>
      </section>
    </section>
  </section>

  <section id="content">
    <h2>Content</h2>

    <p>
      Auction sheets, photographs, videos, and other data received from auction houses are provided solely for the purpose of participating in auctions.
    </p>
    <p>You are prohibited from:</p>
    <ul>
      <li>
        copying, saving, publishing, or distributing auction sheets, photographs, or any parts thereof;
      </li>
      <li>using such materials for marketing, commercial, or public purposes;</li>
      <li>sharing such data with third parties outside your internal purchase decision-making process.</li>
    </ul>
  </section>

  <section id="terms-update">
    <h2>Updates to the Terms</h2>

    <p>
      The Operator may, at any time, unilaterally amend, supplement, or update these Terms, including any sections, appendices, and links, without prior agreement with you.
    </p>
    <p>The updated version of the Terms enters into force upon publication on the Platform.</p>
    <p>
      Continued use of the Platform after publication of the updated Terms constitutes your full and unconditional acceptance of all amendments and supplements, regardless of whether you have reviewed them.
    </p>
    <p>
      If you disagree with the updated Terms, you must immediately cease using the Platform. Cessation of use does not release you from obligations that arose before the termination date, including payments, penalties, expenses, and other obligations.
    </p>
    <p>
      The Operator is not required to send individual notifications of changes to the Terms. Publication of the updated version on the Platform is a sufficient and proper method of notification.
    </p>
  </section>

  <section id="communications">
    <h2>Communications and Disputes</h2>

    <p>
      If any questions, disagreements, claims, or disputes arise in connection with use of the Platform, participation in auctions, auction results, settlements, or other aspects of interaction, you must first contact the Operator.
    </p>
    <p>
      Publication by you or persons acting on your behalf of negative, defamatory, or misleading statements, reviews, comments, or materials about the Platform, the Operator, the auction houses, or auction results in public sources (including but not limited to social media, messengers, forums, review websites, and mass media) without first contacting the Operator is deemed a breach of these Terms.
    </p>
    <p>
      In such a case, the Operator may deem the relevant transaction completed and closed; discontinue consulting/service/other support for the relevant lot or transaction; and suspend or restrict further service to you within the Platform.
    </p>
  </section>

  <section id="applicable-law">
    <h2>Governing Law and Jurisdiction</h2>

    <p>
      These Terms and all relationships arising from use of the Platform, participation in auctions, bid submission, vehicle purchases, and performance of obligations are governed exclusively by the laws of Japan.
    </p>
    <p>
      All disputes, disagreements, or claims arising out of or in connection with these Terms shall be resolved exclusively in the courts of Japan.
    </p>
    <p>
      Exclusive territorial jurisdiction is determined by the Operator's location unless otherwise required by mandatory provisions of Japanese law.
    </p>
  </section>

  <section id="language">
    <h2>Language and Priority of Versions</h2>

    <p>These Terms are drafted in English.</p>
    <p>
      If the Platform provides translations of these Terms into other languages, such translations are for informational purposes only.
    </p>
    <p>
      In the event of discrepancies, contradictions, or differences in interpretation between language versions, the version published on the Platform and recognized by the Operator as the primary version shall prevail.
    </p>
  </section>

  <section id="final-provisions">
    <h2>Final Provisions</h2>

    <p>
      These Terms constitute a public offer under applicable law.
    </p>
    <p>Acceptance of this offer (the "Acceptance") is deemed to occur upon:</p>
    <ul>
      <li>registration on the Platform;</li>
      <li>confirmation of acceptance of the Terms;</li>
      <li>submission of any bid, confirmation, or other action through the Platform.</li>
    </ul>

    <p>
      From the moment of Acceptance, these Terms acquire legal force as an agreement between you and the Operator without the need to sign a separate document.
    </p>
  </section>
</article>
